
import {
  convertDecimalWithComma,
  ellipsisString,
  formatDateNormal
} from "@/app/infrastructures/misc/Utils";
import { ShipmentBooking } from "@/domain/entities/ShipmentBooking";
import { Options, prop, Vue } from "vue-class-component";
import copy from "copy-text-to-clipboard";
import mapProductTypeName from "@/app/infrastructures/misc/common-library/ProductTypeNameMapper";
import Notification from "@/app/ui/components/notification/index.vue";
import { ListPickupV2Controller } from "@/app/ui/controllers/ListPickupV2Controller";
import { ListSttRequestPickupRequest } from "@/data/payload/api/ListPickupV2Request";
import { debounce } from "@/app/infrastructures/misc/common-library/Debounce";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

class Props {
  modelValue = prop<boolean>({ default: false, type: Boolean, required: true });
  bkdCount = prop<number>({ default: 0, type: Number, required: true });
}

@Options({
  components: {
    Notification
  },
  emits: ["update:modelValue", "selection-change"],
  watch: {
    modelValue(val: boolean) {
      if (val) {
        if (!this.allItems.length && !this.isLoadingLoop) {
          this.fetchAllPages();
        } else {
          this.restoreTableSelection();
        }
      }
    },
    selectedItems: {
      deep: true,
      handler() {
        if (!this.isRestoringSelection) {
          this.persistedSelectedItems = [...this.selectedItems];
          this.emitSelectionChange();
        }
      }
    }
  }
})
class ModalSttPickupListDhl extends Vue.with(Props) {
  isLoadingLoop = false;
  fetchError = false;
  searchQuery = "";
  allItems: ShipmentBooking[] = [];
  selectedItems: ShipmentBooking[] = [];
  persistedSelectedItems: ShipmentBooking[] = [];
  isRestoringSelection = false;
  notification = false;

  get visible(): boolean {
    return this.modelValue;
  }

  set visible(val: boolean) {
    this.$emit("update:modelValue", val);
  }

  get totalEligibleCount(): number {
    return this.allItems.length || this.bkdCount;
  }

  get selectedAwbCount(): number {
    return this.selectedItems.length;
  }

  get isErrorList(): boolean {
    return !this.filteredItems.length;
  }

  get isEmpty(): boolean {
    return !this.searchQuery && !this.allItems.length;
  }

  // In-memory filter
  onSearch = debounce((value: string) => {
    this.searchQuery = value;
  });

  get filteredItems(): ShipmentBooking[] {
    if (!this.searchQuery) return this.allItems;
    const q = this.searchQuery.toLowerCase().trim();
    return this.allItems.filter(
      (item) =>
        item.sttNo?.toLowerCase().includes(q) ||
        item.sttShipmentId?.toLowerCase().includes(q) ||
        item.sttDestinationCityName?.toLowerCase().includes(q) ||
        item.sttDestinationCity?.toLowerCase().includes(q)
    );
  }

  // Loop Fetch algorithm
  async fetchAllPages(): Promise<void> {
    try {
      this.isLoadingLoop = true;
      this.isRestoringSelection = true;
      this.fetchError = false;
      this.searchQuery = "";
      this.allItems = [];
      this.selectedItems = [];
      this.persistedSelectedItems = [];
      MainAppController.closeErrorMessage();

      let page = 1;
      const limit = 100;
      let availableRecords = 0;
      let hasMore = true;

      while (hasMore) {
        const res = await ListPickupV2Controller.getCargoTransportType(
          new ListSttRequestPickupRequest({
            page,
            limit,
            status: "BKD",
            isUnlinkedShipment: true
          })
        );

        const items = res.data || [];
        availableRecords = res.pagination?.totalData ?? 0;

        items.forEach((item: ShipmentBooking) => {
          if (
            !this.allItems.some((existing) => existing.sttNo === item.sttNo)
          ) {
            this.allItems.push(item);
          }
        });

        if (items.length === 0 || availableRecords <= items.length) {
          hasMore = false;
        } else {
          page++;
        }
      }

      // Default select all
      this.selectedItems = [...this.allItems];
      this.persistedSelectedItems = [...this.allItems];
      await this.$nextTick();
      this.syncTableSelection();
      await this.$nextTick();
      this.isRestoringSelection = false;
      this.emitSelectionChange();
    } catch (error) {
      this.fetchError = true;
    } finally {
      this.isRestoringSelection = false;
      this.isLoadingLoop = false;
    }
  }

  get checkedSttNos(): Set<string> {
    return new Set(
      this.selectedItems.map((item: ShipmentBooking) => item.sttNo)
    );
  }

  emitSelectionChange(): void {
    this.$emit("selection-change", {
      checkedSttNos: this.checkedSttNos,
      totalEligibleCount: this.allItems.length,
      allItems: this.allItems
    });
  }

  syncTableSelection(): void {
    const table = this.$refs.tableV2 as any;
    if (!table) return;

    table.selectedCheckbox = [...this.persistedSelectedItems];
    table.isSelectAll =
      this.allItems.length > 0 &&
      this.persistedSelectedItems.length === this.allItems.length;
  }

  async restoreTableSelection(): Promise<void> {
    this.isRestoringSelection = true;
    this.selectedItems = [...this.persistedSelectedItems];
    await this.$nextTick();
    this.syncTableSelection();
    await this.$nextTick();
    this.isRestoringSelection = false;
    this.emitSelectionChange();
  }
  get columnsOne() {
    return [
      {
        key: "actionable_column",
        name: `<div class='flex flex-col leading-5 justify-start'>
                <span>
                  ${this.$t(
                    "pickupV2.modalSttPickupList.column.awbAndShipment"
                  )}
                </span>
              </div>`,
        styleHead: "w-60 text-left whitespace-no-wrap",
        styleBody: "text-black-lp-300 text-center",
        render: (item: ShipmentBooking) => {
          return `
            <div class="flex flex-col leading-5">
              <div class="flex items-center space-x-1">
                <div class='text-left text-black-lp-300 font-normal'>
                  ${item.sttNo}
                </div>
                ${
                  item.sttIsCod
                    ? item.sttIsDfod
                      ? `<img src="${require("@/app/ui/assets/svg/new-dfod.svg")}" class="h-2.5" />`
                      : `<img src="${require("@/app/ui/assets/svg/new-cod.svg")}" class="h-2.5" />`
                    : ""
                }
              </div>
              ${
                item.sttShipmentId
                  ? `<div class="text-black-lp-300">${item.sttShipmentId}</div>`
                  : ""
              }
            </div>
        `;
        },
        showButton: () => true,
        icon: "copy-clipboard",
        iconStyle: "ml-1",
        iconColor: "red-lp-100",
        onClickValue: (item: ShipmentBooking) => {
          const result = copy(item.sttNo);
          if (result) {
            this.setNotification();
          }
        },
        iconTooltip: () => this.$t("pickupV2.modalSttPickupList.copyStt")
      }
    ];
  }

  get columnsTwo() {
    return [
      {
        name: this.$t("pickupV2.modalSttPickupList.column.lastStatus"),
        styleHead: "w-40 text-left",
        render: (item: ShipmentBooking) => {
          return `<div class="text-black-lp-300 flex">
                    <div class="rounded capitalize px-2 py-0 ${this.mapLastStatusColor(
                      item.sttLastStatusId
                    )}">
                        ${item.sttLastStatusId}
                    </div>
                  </div>`;
        },
        toolTip: (item: ShipmentBooking) => {
          if (item.sttLastStatusId === "CODREJ") {
            return `<div class='flex text-black-lp-300 text-12px max-w-xs'>${item.dexCodrejReason}</div>`;
          }
          if (item.sttLastStatusId === "DEX") {
            return `
              <div class="text-xs">
                <h5 class="text-gray-lp-300 font-semibold">Percobaan DEX ke ${item.totalSttDex}</h5>
                <p class="text-gray-lp-600">${item.dexCodrejReason}</p>
              </div>
            `;
          }
        }
      },
      {
        name: this.$t("pickupV2.modalSttPickupList.column.destinationCity"),
        styleHead: "w-40 text-left",
        render: (item: ShipmentBooking) => {
          return `<div class="break-all text-black-lp-300 line-clamp-2" title="${item.sttDestinationCity} - ${item.sttDestinationCityName}">${item.sttDestinationCity} - ${item.sttDestinationCityName}</div>`;
        }
      }
    ];
  }

  get columnThree() {
    return [
      {
        name: `<div class='flex flex-col leading-5 justify-start'>
                <span>
                  ${this.$t("pickupV2.modalSttPickupList.column.productType")}
                </span>
                <span class='text-gray-lp-600 font-normal'>
                  ${this.$t(
                    "pickupV2.modalSttPickupList.column.chargeableWeight"
                  )}
                </span>
              </div>`,
        styleHead: "w-56 text-left",
        render: (item: ShipmentBooking) => {
          return `<div class="flex flex-col leading-5">
                    <div class="capitalize text-black-lp-300">
                      ${mapProductTypeName(item.sttProductType)}
                    </div>
                    <div class='text-gray-lp-600 font-normal'>
                      ${convertDecimalWithComma(item.sttChargeAbleWeight, 2)} kg
                    </div>
                  </div>`;
        }
      },
      {
        name: this.$t("pickupV2.modalSttPickupList.column.createdAt"),
        styleHead: "w-64 text-left",
        render: (item: ShipmentBooking) => {
          return `<div class="text-black-lp-300">
                    ${formatDateNormal(
                      item.sttCreatedAt,
                      "DD MMMM YYYY, HH:mm"
                    )}
                    </div>
                    <div class="capitalize text-gray-lp-500">
                    ${ellipsisString(item.sttCreatedName || "-", 30)}
                  </div>`;
        }
      },
      {
        name: this.$t("pickupV2.modalSttPickupList.column.bookedFor"),
        styleHead: "w-64 text-left",
        render: (item: ShipmentBooking) => {
          return `<div class="text-black-lp-300 line-clamp-2">${item.sttBookedForName ||
            "-"}</div>`;
        },
        vtooltip: (item: ShipmentBooking) => item.sttBookedForName
      }
    ];
  }

  get columns() {
    return [...this.columnsOne, ...this.columnsTwo, ...this.columnThree];
  }

  setNotification() {
    this.notification = false;
    setTimeout(() => {
      this.notification = true;
    }, 10);
  }

  mapLastStatusColor(status: string): string {
    if (!status) return "bg-gray-lp-1400 text-gray-lp-300";
    const uppercaseStatus = status.toUpperCase();
    if (
      [
        "CNX",
        "POD",
        "RTS",
        "CLAIM",
        "CI",
        "RTSHQ",
        "STT ADJUSTED POD",
        "REROUTE"
      ].includes(uppercaseStatus)
    ) {
      return "bg-blue-lp-600 text-blue-lp-700";
    }
    if (
      [
        "DEX",
        "SCRAP",
        "SHORTLAND",
        "MIS-ROUTE",
        "CODREJ",
        "MISSING",
        "DAMAGE",
        "NOT RECEIVE",
        "NOT RECEIVED",
        "MISBOOKING",
        "REJECTED"
      ].includes(uppercaseStatus)
    ) {
      return "bg-red-lp-1300 text-red-lp-510";
    }
    return "bg-gray-lp-1400 text-gray-lp-300";
  }

  get copyStyles() {
    return {
      background: "bg-gray-lp-300",
      text: "text-white",
      message: this.$t("pickupV2.modalSttPickupList.copiedStt"),
      width: "w-300px"
    };
  }
}

export default ModalSttPickupListDhl;
